/* eslint-disable react/no-unescaped-entities */

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, PhoneCall, Download, Calendar } from "lucide-react";
import { createLead } from "@/lib/api";
import { fbq } from "@/lib/tracking";

const titles = {
  call_booking: { title: "Réserver un appel découverte", icon: PhoneCall, desc: "Laissez vos coordonnées, on vous rappelle sous 24h pour parler de votre projet." },
  brochure: { title: "Recevoir la brochure complète", icon: Download, desc: "Recevez immédiatement par email la brochure détaillée de la formation." },
  session: { title: "Réserver ma place", icon: Calendar, desc: "Réservez votre place pour la session présentielle. Notre équipe confirme votre inscription." },
};

const initial = { nom: "", prenom: "", telephone: "", email: "", ville: "", projet: "" };

export default function LeadDialog({ open, onClose, source = "call_booking", meta = {} }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setForm(initial); }, [open]);

  if (!titles[source]) source = "call_booking";
  const cfg = titles[source];
  const Icon = cfg.icon;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const projet = form.projet || (meta.plan ? `Intéressé par la formule ${meta.plan}` : "Demande via " + source);
      await createLead({
        ...form,
        projet,
        source,
        session_label: meta.session_label,
      });
      fbq.lead({ content_name: source, content_category: meta.plan || meta.session_label || "general" });
      toast.success("Demande enregistrée !", {
        description: source === "brochure"
          ? "La brochure arrive dans votre boîte mail."
          : "Notre équipe vous recontacte sous 24h.",
      });
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Une erreur est survenue.";
      toast.error("Échec", { description: typeof msg === "string" ? msg : "Vérifiez vos informations." });
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-black border border-zinc-800 text-white px-4 py-3 focus:border-[#FFD60A] focus:outline-none focus:ring-1 focus:ring-[#FFD60A] placeholder:text-zinc-600";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border border-[#FFD60A]/40 max-w-lg p-0 gap-0 rounded-none" data-testid="lead-dialog">
        <DialogHeader className="px-8 pt-8 pb-6 border-b border-zinc-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#FFD60A] flex items-center justify-center shrink-0">
              <Icon size={20} className="text-black" />
            </div>
            <div>
              <DialogTitle className="font-display text-2xl md:text-3xl text-white uppercase tracking-wide leading-tight">
                {cfg.title}
              </DialogTitle>
              {meta.session_label && (
                <div className="mt-1 font-mono text-xs text-[#FFD60A] tracking-widest uppercase">{meta.session_label}</div>
              )}
            </div>
          </div>
          <DialogDescription className="text-zinc-400 mt-4">{cfg.desc}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-8 space-y-4" data-testid="lead-dialog-form">
          <div className="grid grid-cols-2 gap-4">
            <input name="prenom" required placeholder="Prénom" value={form.prenom} onChange={handleChange} className={inputCls} data-testid="dialog-input-prenom" />
            <input name="nom" required placeholder="Nom" value={form.nom} onChange={handleChange} className={inputCls} data-testid="dialog-input-nom" />
          </div>
          <input type="email" name="email" required placeholder="Email" value={form.email} onChange={handleChange} className={inputCls} data-testid="dialog-input-email" />
          <input name="telephone" required placeholder="Téléphone" value={form.telephone} onChange={handleChange} className={inputCls} data-testid="dialog-input-phone" />
          <input name="ville" required placeholder="Ville" value={form.ville} onChange={handleChange} className={inputCls} data-testid="dialog-input-ville" />

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 py-4 bg-[#FFD60A] text-black font-bold uppercase tracking-wider hover:bg-yellow-300 transition-all disabled:opacity-50"
            data-testid="dialog-submit-button"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Envoi…</> : cfg.title}
          </button>
          <p className="text-xs text-zinc-500 text-center">Vos données restent confidentielles.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
